import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stepper,
  Step,
  StepLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getAdminDisputeDetail,
  resolveDispute,
} from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import OrderChangeLog from "../orders/OrderChangeLog";
import OrderDeliveryTracking from "../orders/OrderDeliveryTracking";
import ProofImages from "../orders/ProofImages";
import OrderPriceNegotiation from "../orders/OrderPriceNegotiation";
import OrderCancellation from "../orders/OrderCancellation";
import OrderHandoverAttempts from "../orders/OrderHandoverAttempts";

const formatCurrency = (cents, currency) =>
  currency
    ? `${currency} ${(cents / 100).toFixed(2)}`
    : `${(cents / 100).toFixed(2)}`;

const accountStatusColor = (status) =>
  status === "ACTIVE"
    ? "success"
    : status === "SUSPENDED"
      ? "warning"
      : "error";

const DisputeDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [resolution, setResolution] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [resolutionMessage, setResolutionMessage] = useState("");
  const [snackbar, setSnackbar] = useState(null);

  const {
    data: order,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-dispute", orderId],
    queryFn: () => getAdminDisputeDetail(orderId),
  });

  const mutation = useMutation({
    mutationFn: () =>
      resolveDispute(orderId, { resolution, adminNotes, resolutionMessage }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-disputes"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setSnackbar({ severity: "success", message: "Dispute resolved." });
      setTimeout(() => navigate(frontendRoutes.ADMIN_DISPUTES), 1500);
    },
    onError: (err) => {
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed to resolve dispute.",
      });
    },
  });

  if (isLoading) return <CircularProgress />;
  if (error || !order) return <Alert severity="error">Order not found.</Alert>;

  const isResolved = order.status !== "DISPUTED";
  const raisedBy = order.dispute?.raisedBy || "USER";
  const seller = order.shop?.createdBy;

  const timelineSteps = [
    { label: "Order placed", date: order.createdAt },
    { label: `Dispute raised by ${raisedBy}`, date: order.dispute?.createdAt },
    ...(isResolved
      ? [
          {
            label: `Resolved: ${order.status}`,
            date: order.dispute?.resolvedAt,
          },
        ]
      : []),
  ];

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(frontendRoutes.ADMIN_DISPUTES)}
        sx={{ mb: 2 }}
      >
        Back to Disputes
      </Button>

      <Typography variant="h5" fontWeight={700} mb={3}>
        Dispute — Order #{order.orderNumber}
      </Typography>

      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={7}>
          {/* Order summary */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                Order Summary
              </Typography>
              <Typography variant="body2">
                Status: <Chip label={order.status} size="small" />
              </Typography>
              <Typography variant="body2">
                Total: {formatCurrency(order.subtotalCents, order.currency)}
              </Typography>
              <Typography variant="body2">
                Delivery: {order.delivery?.deliveryMethod || "—"}
              </Typography>
              <Typography variant="body2">
                Payment method: {order.payment?.paymentMethod || "—"}
              </Typography>
              <Typography variant="body2">
                Payment timing: {order.payment?.paymentTiming || "—"}
              </Typography>
              <Typography variant="body2">
                Payment status: {order.payment?.paymentStatus || "—"}
              </Typography>
              {order.payment?.paymentInstructionsSnapshot && (
                <Box mt={1} p={1} bgcolor="grey.100" borderRadius={1}>
                  <Typography variant="caption" fontFamily="monospace">
                    {order.payment.paymentInstructionsSnapshot}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" fontWeight={600} mb={0.5}>
                Items
              </Typography>
              {order.items?.map((item, i) => (
                <Box key={i}>
                  <Typography variant="body2">
                    {item.productName} × {item.quantity} —{" "}
                    {formatCurrency(item.lineTotalCents, item.currency)}
                  </Typography>
                  {item.note && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ pl: 1 }}
                    >
                      Note: {item.note}
                    </Typography>
                  )}
                </Box>
              ))}
              {order.customerNote && (
                <Typography variant="body2" mt={1}>
                  Buyer note: {order.customerNote}
                </Typography>
              )}
              {order.sellerNote && (
                <Typography variant="body2" mt={1}>
                  Seller note: {order.sellerNote}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Dispute details */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                Dispute Details
              </Typography>
              <Typography variant="body2">
                Raised by: <strong>{raisedBy}</strong>
              </Typography>
              <Typography variant="body2">
                Stage:{" "}
                <Chip
                  label={order.dispute?.previousStatus || "—"}
                  size="small"
                  color={
                    order.dispute?.previousStatus === "DELIVERED"
                      ? "default"
                      : "warning"
                  }
                />
                {order.dispute?.previousStatus &&
                  order.dispute.previousStatus !== "DELIVERED" && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="warning.dark"
                      ml={1}
                    >
                      (pre-delivery — buyer/seller had no other recourse)
                    </Typography>
                  )}
              </Typography>
              <Typography variant="body2">
                Reason:{" "}
                <Chip
                  label={order.dispute?.reason?.replace(/_/g, " ")}
                  size="small"
                />
              </Typography>
              {order.dispute?.description && (
                <Typography variant="body2" mt={1}>
                  {order.dispute.description}
                </Typography>
              )}
              {isResolved && order.dispute?.adminNotes && (
                <Box mt={1} p={1} bgcolor="grey.100" borderRadius={1}>
                  <Typography variant="caption">
                    <strong>Internal note (not shown to buyer/seller):</strong>{" "}
                    {order.dispute.adminNotes}
                  </Typography>
                </Box>
              )}
              {isResolved && order.dispute?.resolutionMessage && (
                <Box mt={1} p={1} bgcolor="info.light" borderRadius={1}>
                  <Typography variant="caption">
                    <strong>Note sent to buyer/seller:</strong>{" "}
                    {order.dispute.resolutionMessage}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <OrderPriceNegotiation order={order} />

          <OrderCancellation order={order} />

          <OrderHandoverAttempts order={order} />

          <OrderDeliveryTracking order={order} />

          <OrderChangeLog order={order} />

          {/* Payment proof images */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                Payment Evidence
              </Typography>
              <ProofImages
                title="Buyer's Payment Proof"
                images={order.payment?.buyerPaymentProofImages}
              />
              <ProofImages
                title="Seller's Counter-Proof"
                images={order.payment?.sellerPaymentProofImages}
              />
              {!order.payment?.buyerPaymentProofImages?.length &&
                !order.payment?.sellerPaymentProofImages?.length && (
                  <Typography variant="body2" color="text.secondary">
                    No proof images uploaded.
                  </Typography>
                )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={5}>
          {/* Buyer */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={0.5}>
                Buyer
              </Typography>
              <Typography variant="body2">{order.user?.name || "—"}</Typography>
              <Typography variant="body2">
                {order.user?.email || order.user?.phoneNumber || "—"}
              </Typography>
              <Chip
                label={order.user?.accountStatus || "ACTIVE"}
                color={accountStatusColor(order.user?.accountStatus)}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </CardContent>
          </Card>

          {/* Seller */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={0.5}>
                Seller / Shop
              </Typography>
              <Typography variant="body2">
                {order.shop?.shopName || "—"}
              </Typography>
              {seller && (
                <>
                  <Typography variant="body2">{seller.name}</Typography>
                  <Typography variant="body2">
                    {seller.email || seller.phoneNumber || "—"}
                  </Typography>
                  <Chip
                    label={seller.accountStatus || "ACTIVE"}
                    color={accountStatusColor(seller.accountStatus)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                Timeline
              </Typography>
              <Stepper
                orientation="vertical"
                activeStep={timelineSteps.length - 1}
              >
                {timelineSteps.map(({ label, date }, i) => (
                  <Step key={i} completed>
                    <StepLabel>
                      <Typography variant="body2">{label}</Typography>
                      {date && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(date).toLocaleString()}
                        </Typography>
                      )}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>

          {/* Resolution form */}
          {!isResolved && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography fontWeight={600} mb={1}>
                  Resolve Dispute
                </Typography>
                <RadioGroup
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                >
                  <FormControlLabel
                    value="FULFILLED"
                    control={<Radio />}
                    label="Fulfilled — seller wins, payment confirmed"
                  />
                  <FormControlLabel
                    value="REFUNDED"
                    control={<Radio />}
                    label="Refunded — buyer wins, stock released"
                  />
                  {order.cancellation?.agreedAt &&
                    !order.cancellation?.completedAt && (
                      <FormControlLabel
                        value="CANCELLED"
                        control={<Radio />}
                        label="Cancelled by agreement — refund verified, not counted against the seller"
                      />
                    )}
                  <FormControlLabel
                    value="REOPENED"
                    control={<Radio />}
                    label={`Reopened — issue resolved, order continues as ${order.dispute?.previousStatus || "IN_PROGRESS"}`}
                  />
                </RadioGroup>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Internal note (admin only, required)"
                  helperText="For record-keeping and process improvement — never shown to the buyer or seller."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  sx={{ mt: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Note to buyer/seller (optional)"
                  helperText="Shown in-app and included in the resolution notification."
                  value={resolutionMessage}
                  onChange={(e) => setResolutionMessage(e.target.value)}
                  sx={{ mt: 2 }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={
                    !resolution || !adminNotes.trim() || mutation.isPending
                  }
                  onClick={() => mutation.mutate()}
                >
                  {mutation.isPending ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Submit Resolution"
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
          {isResolved && (
            <Alert severity="success">
              Resolved as <strong>{order.status}</strong>
            </Alert>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DisputeDetail;
